import { setLocalStorage } from "@/utils/localstorage";

import { AnalyticsState } from "./analytics-state";
import { Helpers } from "./helpers";

/**
 * Module for GUPS.
 * @module GUPS
 */
export class GUPS {
    private static readonly CROSS_DOMAIN_UTM_EXPIRY = 3600000; // 1hr in ms

    private static sendGUPSQuery(
        body: string | null,
        callback: null | ((msg: string) => void)
    ) {
        const gupsEndpoint =
            process.env["NODE_ENV"] === "production"
                ? "https://www.atlassian.com/gateway/api/graphql"
                : "https://wac.stg.internal.atlassian.com/gateway/api/graphql";

        const headers = [
            { header: "Content-type", value: "application/json" },
            { header: "x-use-case", value: "voltron-marketing-context" }
        ];

        Helpers.makeRestRequest(
            "POST",
            gupsEndpoint,
            headers,
            body,
            false,
            callback
        );
    }

    public static getMarketingContext(
        anonymousId: string | null,
        sessionId: string | null,
        domain: string | null,
        localLastUpdated: string | null,
        nullMarketingContext: string
    ) {
        const query = `query getUnifiedProfile($anonymousId: ID) {
            growthUnifiedProfile_getUnifiedProfile(
                anonymousId: $anonymousId
            ){
                marketingContext {
                    sessionId, 
                    domain,
                    lastUpdated,
                    utm {
                        sfdcCampaignId,
                        medium,
                        source,
                        campaign,
                        content
                    }
                }
            }
        }`;

        const variables = { anonymousId: anonymousId };

        const payload = JSON.stringify({
            query: query,
            variables: variables
        });

        const callback = function (result: string) {
            if (result === "error") {
                console.error(
                    "Failed request to retrieve marketingContext from GUPS"
                );
            } else {
                const parsedResponse = JSON.parse(result);
                const marketingContext =
                    parsedResponse.data.growthUnifiedProfile_getUnifiedProfile
                        .marketingContext;

                let validSameDomainUtms;
                let validCrossDomainUtms;

                if (marketingContext) {
                    validSameDomainUtms =
                        domain === marketingContext.domain &&
                        sessionId === marketingContext.sessionId;

                    validCrossDomainUtms =
                        domain !== marketingContext.domain &&
                        sessionId &&
                        Number(sessionId) - Number(localLastUpdated) <
                            GUPS.CROSS_DOMAIN_UTM_EXPIRY;
                }

                if (validSameDomainUtms || validCrossDomainUtms) {
                    if (validSameDomainUtms) {
                        setLocalStorage(
                            "marketingContext",
                            JSON.stringify(marketingContext)
                        );
                    } else if (validCrossDomainUtms) {
                        setLocalStorage(
                            "marketingContext",
                            JSON.stringify({
                                sessionId: sessionId,
                                domain: domain,
                                lastUpdated: sessionId,
                                utm: marketingContext.utm
                            })
                        );
                    }

                    const gupsUtms = marketingContext.utm;

                    AnalyticsState.utms = {
                        "utm_sfdc-campaign_id": gupsUtms.sfdcCampaignId,
                        "utm_campaign": gupsUtms.campaign,
                        "utm_source": gupsUtms.source,
                        "utm_medium": gupsUtms.medium,
                        "utm_content": gupsUtms.content
                    };
                } else {
                    setLocalStorage("marketingContext", nullMarketingContext);
                    AnalyticsState.utms = null;
                }
            }
        };

        try {
            GUPS.sendGUPSQuery(payload, callback);
        } catch (error) {
            console.error(
                "Failed to retrieve marketingContext from GUPS with error:",
                error
            );
        }
    }

    public static setMarketingContext(
        anonymousId: string | null,
        sessionId: string | null,
        domain: string | null,
        utms: Record<string, string>
    ) {
        const mutation = `mutation SetMarketingContext {
            growthUnifiedProfile_createUnifiedProfile(profile: 
                { 
                    anonymousId: "${anonymousId}", 
                    unifiedProfile: { 
                        marketingContext : {
                            sessionId: "${sessionId}",
                            domain: "${domain}", 
                            utm: {
                                sfdcCampaignId: "${utms["utm_sfdc-campaign_id"]}"
                                source: "${utms["utm_source"]}"
                                medium: "${utms["utm_medium"]}"
                                content: "${utms["utm_content"]}"
                                campaign: "${utms["utm_campaign"]}"
                            }
                        }
                    } 
                }) {
                    marketingContext {
                        lastUpdated
                    }
                }
        }`;

        const payload = JSON.stringify({
            query: mutation
        });

        const callback = function (result: string) {
            if (result === "error") {
                console.error("Failed request to set marketingContext in GUPS");
            } else {
                let parsedResponse;
                try {
                    parsedResponse = JSON.parse(result);
                    const lastUpdated =
                        parsedResponse.data
                            .growthUnifiedProfile_createUnifiedProfile
                            .marketingContext.lastUpdated;

                    setLocalStorage(
                        "marketingContext",
                        JSON.stringify({
                            sessionId: sessionId,
                            domain: domain,
                            lastUpdated: lastUpdated,
                            utm: {
                                sfdcCampaignId: utms["utm_sfdc-campaign_id"],
                                campaign: utms["utm_campaign"],
                                source: utms["utm_source"],
                                medium: utms["utm_medium"],
                                content: utms["utm_content"]
                            }
                        })
                    );
                } catch (error) {
                    console.error(
                        "Failed to retrieve lastUpdated from GUPS with error:",
                        error
                    );
                }
            }
        };

        try {
            GUPS.sendGUPSQuery(payload, callback);
        } catch (error) {
            console.error(
                "Failed to set marketingContext in GUPS with error:",
                error
            );
        }
    }
}
